<template>
    <div class="mfa-setup-form">
        <div class="row">
            <div class="col-md-6">

                <div>
                    <h3>Step 1</h3>

                    <div>
                        Please ensure provided phone number is correct and click on "Send code" button.

                        <div class="row">
                            <div class="col-md-4">
                                <div class="form-group required">
                                    <input
                                        v-maska
                                        data-maska="(###) ###-####"
                                        type="text"
                                        class="form-control"
                                        placeholder="Phone Number"
                                        v-model="form.phone_number"
                                    />
                                    <div class="error-help" v-if="v$.phone_number.$dirty && v$.phone_number.$invalid">
                                        Phone Number is required.
                                    </div>
                                </div>
                            </div>
                        </div>

                        <button class="btn btn-success"
                                :disabled="v$.phone_number.$invalid || isSendingCode"
                                @click="handleRequestMfaCode">
                            Send code
                        </button>
                        <Loader v-if="isSendingCode" wait-text="Sending code..."/>
                    </div>
                </div>
                <br>
                <div>
                    <h3>Step 2</h3>

                    Please enter the {{ codeLabel }} code you received in the field below and click on "Verify code" button to finish the setup.

                    <div v-if="!isLoading">
                        <div class="row">
                            <div class="col-md-4">
                                <div class="form-group">
                                    <input type="text"
                                           v-model="form.authenticator_code"
                                           placeholder="Code"
                                           class="form-control"
                                           @keydown="handleConfirmViaEnter"
                                    >
                                    <div class="error-help" v-if="errorMessage.length > 0">{{ errorMessage }}</div>
                                </div>
                            </div>
                        </div>

                        <button class="btn btn-primary" :disabled="v$.$invalid" @click="handleConfirm">Verify code
                        </button>
                    </div>
                    <div v-else>
                        <Loader wait-text="Verifying..."/>
                    </div>
                </div>
            </div>
            <div>&nbsp;</div>
        </div>
    </div>
</template>

<script>
import required from "vuelidate/lib/validators/required";
import { useForm } from "@/hooks/use-form";
import { vMaska } from "maska"
import Loader from "@/components/Loader.vue";
import { computed, onMounted, ref } from "vue";
import { TYPE_MFA_SMS } from "@/pages/pending-actions/mfa-types";
import { requestMfaCode } from "@/api/login";
import { pattern } from "@/helpers/validators";

export default {
    name: "MfaCodeSetupForm",
    components: { Loader },
    directives: { maska: vMaska },
    props: {
        isLoading: Boolean,
        errorMessage: String,
        type: String,
        phoneNumber: String,
    },
    setup(props, { emit }) {
        const isSendingCode = ref(false);

        const { form, v$ } = useForm({
            props: {
                authenticator_code: '',
                phone_number: '',
            },
            rules: {
                authenticator_code: { required },
                phone_number: {
                    required,
                    pattern: pattern(/^\(\d{3}\) \d{3}-\d{4}$/),
                }
            }
        });

        const handleConfirm = async () => {
            if (v$.value.$invalid) {
                return;
            }

            emit('confirmed', {
                code: form.authenticator_code,
                type: props.type,
            });
        };

        const handleConfirmViaEnter = (e) => {
            if (e.key === "Enter" || e.key === "Return") {
                handleConfirm();
            }
        };

        const handleRequestMfaCode = async () => {
            v$.value.phone_number.$validate();

            if (v$.value.phone_number.$invalid) {
                return;
            }

            isSendingCode.value = true;

            try {
                await requestMfaCode({
                    phone_number: form.phone_number,
                    type: props.type,
                });
            } catch (e) {
                console.error(e);
            }

            isSendingCode.value = false;
        }

        onMounted(() => form.phone_number = props.phoneNumber);

        return {
            codeLabel: computed(() => props.type === TYPE_MFA_SMS ? 'SMS' : 'Voice'),
            isSendingCode,
            handleConfirmViaEnter,
            handleConfirm,
            handleRequestMfaCode,
            form,
            v$
        }
    }
}
</script>

<style scoped lang="scss">
.mfa-setup-form {
    h3 {
        font-size: 16px;
        font-weight: bold;
    }

    .error-help {
        color: red;
    }
}
</style>